import React from "react";
import type { GetStaticProps, NextPage } from "next";
import { Box, Text } from "@chakra-ui/react";
import {
  type Page as SanityPage,
  type IntegrationSimple,
  type Customer,
  type Banner,
  getSimpleIntegrations,
  getPage,
  getStackedLogos,
  getAnnouncementBanner,
  getFeaturedCustomers,
} from "lib/sanity";
import { getPageMetaImage } from "utils";
import { Page } from "components/page";
import { Section, sectionSpacingY } from "components/section";
import { StackedLogos } from "components/logos";
import DestinationMarquee from "components/destination-marquee";
import { AuraCardQuote } from "components/quotes";
import { SecurityFeatures, ComposableCdpAccordion, CustomerStats, IndustryLeaderAwards } from "components/content";
import {
  PlatformFeatureCards,
  ProductScreensSlideshow,
  UseCasesByTeam,
  UseCaseTabs,
} from "components/platform-overview-content";
import { RuleAndTextCta } from "ui/text";
import { SolidPlate } from "ui/plates";
import { HomeHero } from "components/home/hero";

type PageProps = {
  page: SanityPage;
  destinations: IntegrationSimple[];
  announcementBanner?: Banner;
  customerRows: Customer[][];
  featuredStatCustomers: Customer[];
};

const HomeV2: NextPage<PageProps> = ({
  page,
  destinations,
  announcementBanner,
  customerRows,
  featuredStatCustomers,
}) => {
  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
      theme="dark"
      bgColor="new.green.950"
    >
      <HomeHero title={page.title} description={page.description} />

      {customerRows && (
        <StackedLogos customerRows={customerRows} bg="white" pt={{ base: "bigger", md: "biggest", lg: "huge" }} />
      )}

      <PlatformFeatureCards />

      <AuraCardQuote
        mt={{ base: "big", sm: "huge", md: "hugest" }}
        quote={{
          quoteText: (
            <>
              Implementing and onboarding Hightouch took{" "}
              <Text as="span" fontWeight="bold">
                less than a day
              </Text>{" "}
              from start to finish.
            </>
          ),
          author: { name: "Omar Mayar", job: "Senior Data Engineer" },
          company: {
            name: "Calendly",
            logo: "https://cdn.sanity.io/images/pwmfmi47/production/1532304733eca92eb425273517be537567f0ce97-1581x382.svg",
          },
          cta: { text: "Our customers", href: "/customers" },
        }}
      />

      <CustomerStats customers={featuredStatCustomers} my={0} pb="huger" />

      <SolidPlate bgColor="new.blue.950" theme="dark">
        <Text
          as="h2"
          color="new.blue.50"
          textStyle={{ base: "display3", md: "display2", lg: "display1", xl: "poster3" }}
        >
          Data activation made easy
        </Text>

        <ProductScreensSlideshow />

        <UseCasesByTeam />

        <RuleAndTextCta theme="dark" ctaHref="/platform/customer-360">
          Not sure if your data&apos;s ready for action? <Box as="br" display={{ base: "none", md: "inline-block" }} />
          We’ve got you covered.
        </RuleAndTextCta>
      </SolidPlate>

      <Section bg="white">
        <ComposableCdpAccordion sectionTitle="Built on the best foundation — your data warehouse" sectionMaxW="3xl" />
      </Section>

      <SecurityFeatures my={0} />

      <UseCaseTabs />

      <IndustryLeaderAwards />

      <DestinationMarquee destinations={destinations} my={0} py={sectionSpacingY} />
    </Page>
  );
};

export default HomeV2;

export const getStaticProps: GetStaticProps<PageProps> = async () => {
  const [page, destinations, announcementBanner, customerRows, featuredStatCustomers] = await Promise.all([
    getPage("/"),
    getSimpleIntegrations("destination"),
    getAnnouncementBanner(),
    getStackedLogos(),
    getFeaturedCustomers(["petsmart", "wmg", "lucidchart"]),
  ]);

  if (!page) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      page,
      destinations,
      announcementBanner,
      customerRows,
      featuredStatCustomers,
    },
    revalidate: 60,
  };
};
